import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { GeolocationService } from '../../shared/service/geolocation/geolocation.service';
import { Workshop } from '../fleet/mock/models/workshop.model';
import { WorkshopMockService } from '../fleet/mock/workshop-mock.service';

/**
 * Tarjeta de descubrimiento: el taller "slim" del asset + los campos de
 * presentación que la grilla necesita.
 */
export interface DiscoverCard extends Workshop {
  rating: number;
  reviews: number;
  verified: boolean;
  distanceKm: number | null;
  /** Precalculado: evita crear un literal nuevo por ciclo de detección. */
  pos: google.maps.LatLngLiteral;
}

/** Especialidad de la barra de filtros, con su conteo. */
export interface CategoryChip {
  name: string;
  short: string;
  icon: string;
  count: number;
}

/**
 * Home principal de la web — la red de talleres Pitzon: hero de marca con
 * buscador y métricas del catálogo, barra pegajosa de especialidades,
 * grilla de tarjetas con panel de métricas y vista de mapa.
 *
 * Fuente de datos: WorkshopMockService → assets/mocks/workshops.slim.json
 * (475 talleres reales de producción, 248 con foto).
 *
 * ATENCIÓN — datos derivados, no del backend: `rating`, `reviews` y `verified`
 * se calculan de forma determinista a partir del id en decorate() sólo para
 * maquetar la tarjeta. Cuando exista el endpoint real de reseñas hay que
 * reemplazar decorate() por los valores del servidor. `distanceKm` sí es real:
 * se calcula con haversine contra la ubicación que el usuario autorice.
 */
@Component({
  selector: 'app-home-discover',
  templateUrl: './home-discover.component.html',
  styleUrls: ['./home-discover.component.css'],
})
export class HomeDiscoverComponent implements OnInit, OnDestroy {
  /** Etiquetas cortas: el nombre completo de la categoría no cabe en un chip. */
  private static readonly SHORT_LABELS: Record<string, string> = {
    'Mecánica general autos y camionetas': 'Mecánica',
    'Taller de Latonería y Pintura': 'Latonería',
    'Taller de Motos': 'Motos',
    'Taller de Vehículos Pesados': 'Pesados',
    'Taller de Scooters': 'Scooters',
    Autolavado: 'Autolavado',
  };

  private static readonly PAGE_SIZE = 24;

  loading = true;
  showMap = false;

  /** Universo completo ya decorado. */
  private all: DiscoverCard[] = [];
  /** Resultado de aplicar buscador + categoría + filtros. */
  results: DiscoverCard[] = [];
  /** Página visible de `results`. */
  visible: DiscoverCard[] = [];

  categories: CategoryChip[] = [];
  activeCategory: string | null = null;
  query = '';
  onlyWithPhoto = false;

  userLoc: { lat: number; lng: number } | null = null;
  locating = false;
  locationDenied = false;

  favorites = new Set<string>();
  selected: DiscoverCard | null = null;

  /** Espejo del tema del store; alimenta el icono sol/luna del header. */
  isDark = false;

  /** Placeholders del estado de carga. */
  readonly skeletons = Array.from({ length: 12 });

  mapCenter: google.maps.LatLngLiteral = { lat: 3.4328, lng: -76.541 };
  mapZoom = 11;
  readonly mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    clickableIcons: false,
    styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
  };

  /** Pin de taller y pin de "tú estás aquí", en la paleta de la marca. */
  markerOptions: google.maps.MarkerOptions = {};
  userMarkerOptions: google.maps.MarkerOptions = {};

  private page = 1;
  private sub?: Subscription;
  private themeSub?: Subscription;

  constructor(
    private readonly workshops: WorkshopMockService,
    private readonly geo: GeolocationService,
    private readonly storeData: Store<any>,
  ) {
    // do nothing
  }

  ngOnInit(): void {
    this.buildMarkerStyles();

    this.themeSub = this.storeData
      .select((d: any) => d.index)
      .subscribe((index: any) => {
        this.isDark = Boolean(index?.isDarkMode);
      });

    this.sub = this.workshops.getWorkshops().subscribe({
      next: (list) => {
        this.all = list.map((w) => this.decorate(w));
        this.categories = this.buildCategories(list);
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.themeSub?.unsubscribe();
  }

  // ---------------------------------------------------------------- filtros

  selectCategory(name: string | null): void {
    this.activeCategory = this.activeCategory === name ? null : name;
    this.applyFilters();
  }

  onQueryChange(value: string): void {
    this.query = value;
    this.applyFilters();
  }

  toggleWithPhoto(): void {
    this.onlyWithPhoto = !this.onlyWithPhoto;
    this.applyFilters();
  }

  clearFilters(): void {
    this.query = '';
    this.activeCategory = null;
    this.onlyWithPhoto = false;
    this.applyFilters();
  }

  /** Cuántos talleres traen foto: dato del hero. */
  get photoCount(): number {
    return this.all.filter((c) => c.image).length;
  }

  /** Etiqueta corta de la categoría principal de un taller (pastilla de la foto). */
  categoryShort(card: DiscoverCard): string {
    const name = card.categories[0];
    return HomeDiscoverComponent.SHORT_LABELS[name] ?? name ?? 'Taller';
  }

  /** Vista lista/mapa desde el control segmentado. */
  setView(mode: 'list' | 'map'): void {
    if ((mode === 'map') === this.showMap) {
      return;
    }
    this.toggleMap();
  }

  /** Total del universo cargado; se usa en el estado vacío. */
  get totalWorkshops(): number {
    return this.all.length;
  }

  /** Etiqueta corta de la categoría activa. */
  get activeCategoryShort(): string {
    if (!this.activeCategory) {
      return 'Todas las categorías';
    }
    return this.categories.find((c) => c.name === this.activeCategory)?.short ?? this.activeCategory;
  }

  /** Titular de resultados. */
  get resultsTitle(): string {
    const n = this.results.length;
    const noun = n === 1 ? 'taller' : 'talleres';
    if (this.activeCategory) {
      return `${n} ${noun} de ${this.activeCategoryShort.toLowerCase()}`;
    }
    if (this.query.trim()) {
      return `${n} ${noun} para "${this.query.trim()}"`;
    }
    return `${n} ${noun} disponibles`;
  }

  get hasFilters(): boolean {
    return Boolean(this.query.trim()) || this.activeCategory !== null || this.onlyWithPhoto;
  }

  loadMore(): void {
    this.page += 1;
    this.slice();
  }

  get canLoadMore(): boolean {
    return this.visible.length < this.results.length;
  }

  private applyFilters(): void {
    const q = this.query.trim().toLowerCase();

    this.results = this.all.filter((c) => {
      if (this.activeCategory && !c.categories.includes(this.activeCategory)) {
        return false;
      }
      if (this.onlyWithPhoto && !c.image) {
        return false;
      }
      if (q) {
        const haystack = `${c.name} ${c.categories.join(' ')}`.toLowerCase();
        if (!haystack.includes(q)) {
          return false;
        }
      }
      return true;
    });

    this.sortResults();
    this.page = 1;
    this.slice();
  }

  /**
   * Con ubicación autorizada mandan los talleres cercanos; sin ella, primero
   * los que tienen foto (la grilla se ve viva) y luego por calificación.
   */
  private sortResults(): void {
    if (this.userLoc) {
      this.results.sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
      return;
    }
    this.results.sort((a, b) => {
      const photo = Number(Boolean(b.image)) - Number(Boolean(a.image));
      return photo !== 0 ? photo : b.rating - a.rating;
    });
  }

  private slice(): void {
    this.visible = this.results.slice(0, this.page * HomeDiscoverComponent.PAGE_SIZE);
  }

  // ------------------------------------------------------------- ubicación

  async useMyLocation(): Promise<void> {
    if (this.locating) {
      return;
    }
    this.locating = true;
    this.locationDenied = false;

    const position = await this.geo.getCurrentLocation();
    if (position?.coords) {
      this.userLoc = { lat: position.coords.latitude, lng: position.coords.longitude };
      this.mapCenter = { ...this.userLoc };
      this.mapZoom = 12;
      this.all.forEach((c) => {
        c.distanceKm = this.haversine(this.userLoc!.lat, this.userLoc!.lng, c.lat, c.lng);
      });
      this.applyFilters();
    } else {
      this.locationDenied = true;
    }
    this.locating = false;
  }

  /**
   * El script de Google Maps se carga de forma síncrona en index.html, pero
   * comprobamos igual: si no estuviera, los marcadores usan el pin por defecto.
   */
  private buildMarkerStyles(): void {
    if (typeof google === 'undefined' || !google.maps) {
      return;
    }
    this.markerOptions = {
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: '#c5f112',
        fillOpacity: 1,
        strokeColor: '#0e1726',
        strokeWeight: 2,
      },
    };
    this.userMarkerOptions = {
      zIndex: 999,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#0e1726',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      },
    };
  }

  private haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  formatDistance(km: number | null): string {
    if (km === null) {
      return '';
    }
    return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(km < 10 ? 1 : 0)} km`;
  }

  // ------------------------------------------------------------------ mapa

  toggleMap(): void {
    this.showMap = !this.showMap;
    this.selected = null;
    if (this.showMap && !this.userLoc && this.results.length) {
      this.mapCenter = { lat: this.results[0].lat, lng: this.results[0].lng };
      this.mapZoom = 11;
    }
    // Tras cambiar de vista la altura del documento cambia; esperamos al
    // repintado para que el scroll no quede a media altura.
    setTimeout(() => window.scrollTo({ top: 0 }), 0);
  }

  /** Cota de marcadores: 475 pines a la vez ahogan el mapa. */
  get markers(): DiscoverCard[] {
    return this.results.slice(0, 150);
  }

  selectCard(card: DiscoverCard): void {
    this.selected = card;
    this.mapCenter = { lat: card.lat, lng: card.lng };
  }

  // ------------------------------------------------------------------ misc

  toggleFavorite(card: DiscoverCard, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.favorites.has(card.id)) {
      this.favorites.delete(card.id);
    } else {
      this.favorites.add(card.id);
    }
  }

  isFavorite(card: DiscoverCard): boolean {
    return this.favorites.has(card.id);
  }

  /** Si la foto de S3 falla, caemos al tile de marca con el emoji. */
  onImageError(card: DiscoverCard): void {
    card.image = null;
  }

  toggleTheme(): void {
    this.storeData.dispatch({ type: 'toggleTheme', payload: this.isDark ? 'light' : 'dark' });
  }

  scrollToResults(): void {
    document.getElementById('ptz-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  trackById(_index: number, card: DiscoverCard): string {
    return card.id;
  }

  trackByCategory(_index: number, category: CategoryChip): string {
    return category.name;
  }

  // ------------------------------------------------------------- derivados

  private decorate(w: Workshop): DiscoverCard {
    const h = this.hash(w.id);
    return {
      ...w,
      rating: Number((4.2 + (h % 9) / 10).toFixed(1)),
      reviews: 3 + (h % 287),
      verified: h % 5 === 0,
      distanceKm: null,
      pos: { lat: w.lat, lng: w.lng },
    };
  }

  private hash(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) {
      h = (h * 31 + id.charCodeAt(i)) >>> 0;
    }
    return h;
  }

  private buildCategories(list: Workshop[]): CategoryChip[] {
    const map = new Map<string, CategoryChip>();

    list.forEach((w) => {
      w.categories.forEach((name, i) => {
        const found = map.get(name);
        if (found) {
          found.count += 1;
          return;
        }
        map.set(name, {
          name,
          short: HomeDiscoverComponent.SHORT_LABELS[name] ?? name,
          icon: w.icons?.[i] ?? '🔧',
          count: 1,
        });
      });
    });

    return [...map.values()].sort((a, b) => b.count - a.count);
  }
}
